import { useCallback, useEffect, useRef, useState } from "react";
import {
    FalkonDocUploadDateIcon,
    FalkonDocUploadSignIcon,
    FalkonDocInitialIcon,
    FalkonDocUploadTextIcon,
    FalkonDocUploadCheckboxIcon,
} from "../../icon";

interface DroppedField {
    id: string;
    type: string;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
    page?: number;
    assignedRecipient?: string;
}

const EmbeddedFieldComponent: React.FC<{
    field: DroppedField;
    isSelected: boolean;
    onSelect?: (id: string | null) => void;
    onDelete?: (id: string) => void;
    onDragStart?: (field: DroppedField, startPos: { x: number; y: number }) => void;
    onResize?: (id: string, width: number, height: number) => void;
    recipientRestrictions?: Map<string, { canEdit: boolean, canAdd: boolean }>;
    recipientEmails?: { recipient_id: string, email: string; order_number: number }[];
}> = ({ field, isSelected, onSelect, onDelete, onDragStart, onResize, recipientRestrictions, recipientEmails }) => {


    const borderColors = ["#156fb3", "#7A14FF", "#00C48C", "#0091FF", "#FF647C"];
    const [isResizing, setIsResizing] = useState(false);
    const fieldRef = useRef<HTMLDivElement>(null);
    const resizeStartRef = useRef<{
        startX: number;
        startY: number;
        startWidth: number;
        startHeight: number;
    } | null>(null);

    const getRecipientColor = (recipientId: string): string => {
        if (!recipientEmails || !recipientId) return borderColors[0];

        const recipient = recipientEmails.find(r => r.recipient_id === recipientId);
        if (recipient) {
            return borderColors[(recipient.order_number - 1) % borderColors.length];
        }
        return borderColors[0];
    };

    const getRecipientInfo = () => {
        if (!field.assignedRecipient || !recipientEmails) return null;
        return recipientEmails.find(r => r.recipient_id === field.assignedRecipient);
    };

    const recipientInfo = getRecipientInfo();
    const recipientColor = field.assignedRecipient ? getRecipientColor(field.assignedRecipient) : borderColors[0];

    const canEditField = () => {
        if (!field.assignedRecipient || !recipientRestrictions) return true;
        const restrictions = recipientRestrictions.get(field.assignedRecipient);
        return restrictions?.canEdit !== false;
    };

    const isFieldLocked = !canEditField();

    const handleResizeMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing || !resizeStartRef.current || !onResize || isFieldLocked) return;

        const { startX, startY, startWidth, startHeight } = resizeStartRef.current;
        const deltaX = e.clientX - startX;
        const deltaY = e.clientY - startY;

        const minWidth = field.type === 'signature' ? 150 : 80;
        const minHeight = field.type === 'signature' ? 30 : 30;
        const maxWidth = 400;
        const maxHeight = 200;

        const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidth + deltaX));
        const newHeight = Math.min(maxHeight, Math.max(minHeight, startHeight + deltaY));

        onResize(field.id, newWidth, newHeight);
    }, [isResizing, field.id, field.type, onResize, isFieldLocked]);


    const handleResizeMouseUp = useCallback(() => {
        setIsResizing(false);
        resizeStartRef.current = null;
    }, []);


    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleResizeMouseMove);
            document.addEventListener('mouseup', handleResizeMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleResizeMouseMove);
                document.removeEventListener('mouseup', handleResizeMouseUp);
            };
        }
    }, [isResizing, handleResizeMouseMove, handleResizeMouseUp]);

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (onSelect) {
            onSelect(field.id);
        }

        if (e.target === fieldRef.current && onDragStart && !isFieldLocked) {
            const startPos = { x: e.clientX, y: e.clientY };
            onDragStart(field, startPos);
        }
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (isFieldLocked) {
            return;
        }

        if (onDelete) {
            onDelete(field.id);
        }
    };

    const handleResizeMouseDown = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (!onResize || isFieldLocked) return;

        setIsResizing(true);
        resizeStartRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startWidth: field.width,
            startHeight: field.height
        };
    };

    const getFieldIcon = (type: string): JSX.Element => {
        switch (type.toLowerCase()) {
            case 'signature':
                return <FalkonDocUploadSignIcon />;
            case 'initials': 
                return <FalkonDocInitialIcon />;
            case 'text':
                return <FalkonDocUploadTextIcon />;
            case 'date':
                return <FalkonDocUploadDateIcon />;
            case 'checkbox':
                return <FalkonDocUploadCheckboxIcon />;
            default:
                return <FalkonDocUploadTextIcon />; 
        }
    };

    const getFieldStyles = () => {
        const baseColor = recipientColor;
        const isLocked = isFieldLocked;

        if (isLocked) {
            return {
                borderColor: '#6c757d',
                backgroundColor: '#f8f9fa',
                recipientColor: '#6c757d',
                shadowColor: '108, 117, 125',
                opacity: 0.6
            };
        }

        const hex = baseColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        return {
            borderColor: baseColor,
            backgroundColor: `${baseColor}15`,
            recipientColor: baseColor,
            shadowColor: `${r}, ${g}, ${b}`,
            opacity: 1
        };
    };

    const styles = getFieldStyles();

    return (
        <div
            ref={fieldRef}
            className={`field-container ${isSelected ? 'selected' : ''} ${isResizing ? 'resizing' : ''} ${isFieldLocked ? 'locked' : ''}`}
            style={{
                left: field.x,
                top: field.y,
                width: field.width,
                height: field.height,
                opacity: styles.opacity,
                backgroundColor: isSelected ? styles.backgroundColor : `${styles.backgroundColor}cc`,
                borderColor: styles.borderColor,
                boxShadow: isSelected
                    ? `0 8px 25px rgba(${styles.shadowColor}, 0.25), 0 0 0 4px rgba(${styles.shadowColor}, 0.1)`
                    : `0 4px 15px rgba(${styles.shadowColor}, 0.15), 0 0 0 1px rgba(${styles.shadowColor}, 0.05)`,
            }}
            onMouseDown={handleMouseDown}
            onClick={(e) => {
                e.stopPropagation();
                onSelect?.(field.id);
            }}
        >
            <div className="recipient-badge" style={{ backgroundColor: styles.recipientColor, boxShadow: `0 4px 12px rgba(${styles.shadowColor}, 0.3)` }}>
                {recipientInfo?.order_number || '?'}
            </div>

            <div className="recipient-strip" style={{ backgroundColor: styles.recipientColor }} />

            {isFieldLocked && (
                <div className="lock-icon">
                    🔒
                </div>
            )}

            <div className="field-label-container">
                <span
                    className={`field-label-icon d-flex align-center justify-center ${isFieldLocked ? 'field-label-icon--locked' : ''}`}
                    style={{
                        width: "18px",
                        height: "18px"
                    }}
                >
                    {getFieldIcon(field.type)}
                </span>
                <span
                    className={`
                field-label-text 
                ${isFieldLocked ? 'field-label-text--locked' : ''} 
                ${isSelected ? 'field-label-text--selected' : ''}
                `}
                    style={{
                        color: isFieldLocked ? undefined : styles.recipientColor,
                        maxWidth: `${field.width - 60}px`,
                        ...(isSelected && {
                            ['--selected-text-shadow' as any]: `0 1px 3px rgba(${styles.shadowColor}, 0.3)`
                        })
                    }}
                >
                    {field.label}
                </span>
            </div>

            {isSelected && !isFieldLocked && (
                <>
                    {onDelete && (
                        <button
                            onClick={handleDelete}
                            className="dropped-field-delete-button"
                        >
                            ×
                        </button>
                    )}

                    {onResize && (
                        <div
                            className="resize-handle"
                            onMouseDown={handleResizeMouseDown}
                            style={{
                                backgroundColor: styles.recipientColor,
                                boxShadow: `0 4px 12px rgba(${styles.shadowColor}, 0.3)`
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.boxShadow = `0 6px 16px rgba(${styles.shadowColor}, 0.4)`;
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.boxShadow = `0 4px 12px rgba(${styles.shadowColor}, 0.3)`;
                            }}
                        >
                            <div className="resize-handle-icon" />
                        </div>
                    )}
                </>
            )}

            {isSelected && (
                <div
                    className="field-selected-tooltip"
                    style={{ border: `1px solid ${styles.recipientColor}66` }}
                >
                    {recipientInfo && (
                        <div className="tooltip-recipient">
                            <div className="tooltip-assigned">
                                <strong>Assigned to:</strong> {recipientInfo.email}
                            </div>
                            {isFieldLocked && (
                                <div className="tooltip-locked">
                                    🔒 Locked (Recipient invited)
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {field.width < 100 && (
                <div
                    className="field-label-badge"
                    style={{
                        backgroundColor: styles.recipientColor,
                        boxShadow: `0 2px 4px rgba(${styles.shadowColor}, 0.2)`
                    }}
                >
                    {field.type.substring(0, 3)}
                </div>
            )}

        </div>
    );
};

export default EmbeddedFieldComponent;